import { useForm } from "@inertiajs/react";
import InvoiceTypeBadge from "@/components/invoice-type-badge";
import { PageTitle } from "@/components/page-title";
import PaymentTransactionBadge from "@/components/payment-transaction-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Invoice, PropsWithUser, PaymentTransaction } from "@/types";
import { formatDate } from "@/utils/format-date";
import { formatMoney } from "@/utils/format-money";
import { PropsWithChildren } from "react";

type Props = {
  payment_transactions: PaymentTransaction[];
  invoices: Invoice[];
};

type ReconciliationFormData = {
  payment_transaction_ids: number[];
  invoice_ids: number[];
};

function ReconciliationNew({
  payment_transactions: paymentTransactions,
  invoices,
}: PropsWithUser<Props>) {
  const { data, setData, post, processing, transform } =
    useForm<ReconciliationFormData>({
      payment_transaction_ids: [],
      invoice_ids: [],
    });

  const reconcileButtonDisabled =
    processing || data.payment_transaction_ids.length === 0;

  transform((data) => {
    return { reconciliation: data };
  });

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    post("/reconciliations", {
      preserveScroll: true,
      onSuccess: () => {
        setData("payment_transaction_ids", []);
        setData("invoice_ids", []);
      },
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <PageTitle>New reconciliation</PageTitle>
        <Button
          type="submit"
          form="reconciliation-form"
          disabled={reconcileButtonDisabled}
        >
          Reconcile
        </Button>
      </div>

      <form
        id="reconciliation-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 items-start gap-8"
      >
        <div>
          <h2 className="text-xl font-medium">Transactions</h2>

          <div className="mt-4 grid max-h-[calc(100vh-200px)] gap-4 overflow-scroll">
            {paymentTransactions.map((paymentTransaction) => {
              return (
                <PaymentTransactionItem
                  key={paymentTransaction.id}
                  paymentTransaction={paymentTransaction}
                  onSelect={(selected) => {
                    setData(
                      "payment_transaction_ids",
                      selected
                        ? [
                            ...data.payment_transaction_ids,
                            paymentTransaction.id,
                          ]
                        : data.payment_transaction_ids.filter(
                            (id) => id !== paymentTransaction.id,
                          ),
                    );
                  }}
                />
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium">Invoices</h2>

          <div className="mt-4 grid max-h-[calc(100vh-200px)] gap-4 overflow-scroll">
            {invoices.map((invoice) => {
              return (
                <InvoiceItem
                  key={invoice.id}
                  invoice={invoice}
                  onSelect={(selected) => {
                    setData(
                      "invoice_ids",
                      selected
                        ? [...data.invoice_ids, invoice.id]
                        : data.invoice_ids.filter((id) => id !== invoice.id),
                    );
                  }}
                />
              );
            })}
          </div>
        </div>
      </form>
    </div>
  );
}

interface ItemProps extends PropsWithChildren {
  onSelect: (selected: boolean) => void;
}

function Item({ children, onSelect }: ItemProps) {
  return (
    <Label className="hover:bg-accent/50 flex items-start gap-4 rounded-lg border p-4 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
      <Checkbox
        className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
        onCheckedChange={(checked) => onSelect(Boolean(checked))}
      />
      <div className="flex-1">{children}</div>
    </Label>
  );
}

function ItemProperty({
  className,
  label,
  value,
}: {
  className?: string;
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-muted-foreground font-sans text-sm leading-none font-medium">
        {label}
      </p>
      {typeof value === "string" ? <p className="text-sm">{value}</p> : value}
    </div>
  );
}

function PaymentTransactionItem({
  paymentTransaction,
  onSelect,
}: {
  paymentTransaction: PaymentTransaction;
  onSelect: (selected: boolean) => void;
}) {
  return (
    <Item onSelect={onSelect}>
      <div className="grid grid-cols-[120px_160px_1fr_1fr_1fr]">
        <ItemProperty
          label="Type"
          value={<PaymentTransactionBadge type={paymentTransaction.type} />}
        />

        <ItemProperty
          label="Date"
          value={formatDate(paymentTransaction.date)}
        />

        <ItemProperty
          label="Merchant"
          value={paymentTransaction.counterparty.name}
        />

        <ItemProperty label="Reason" value={paymentTransaction.reason} />

        <ItemProperty
          className="text-right font-mono font-bold"
          label="Amount"
          value={formatMoney(paymentTransaction.amount)}
        />
      </div>
    </Item>
  );
}

function InvoiceItem({
  invoice,
  onSelect,
}: {
  invoice: Invoice;
  onSelect: (selected: boolean) => void;
}) {
  return (
    <Item onSelect={onSelect}>
      <div className="grid grid-cols-[120px_160px_160px_1fr_1fr_1fr]">
        <ItemProperty
          label="Type"
          value={<InvoiceTypeBadge type={invoice.type} />}
        />

        <ItemProperty
          label="Issue date"
          value={formatDate(invoice.issue_date)}
        />

        <ItemProperty label="Tax event date" value={invoice.tax_event_date} />

        <ItemProperty label="Merchant" value={invoice.merchant.name} />

        <ItemProperty label="Number" value={invoice.number} />

        <ItemProperty
          className="text-right font-mono font-bold"
          label="Amount"
          value={formatMoney(invoice.total_amount)}
        />
      </div>
    </Item>
  );
}

export default ReconciliationNew;
